import { useState, type ReactNode } from 'react';
import './ThumbsRating.css';

export type ThumbsValue = 'positive' | 'negative';

interface ThumbsRatingProps {
    /** Persisted rating to render selected. */
    initialRating?: ThumbsValue | null;
    /** Async submitter. Errors are swallowed — rating is non-critical UX. */
    onRate: (rating: ThumbsValue) => Promise<void>;
    /** Optional follow-up — invoked after `onRate` resolves successfully. Lets
     *  the host chain a comment popup, toast, etc. Errors here do NOT roll
     *  back the rating state since the rating itself was persisted. */
    onAfterRate?: (rating: ThumbsValue) => void | Promise<void>;
    /** Visual size — 'md' is the default chip size for use inside action rows. */
    size?: 'sm' | 'md';
    /** Tooltip overrides for niche contexts. */
    positiveTitle?: string;
    negativeTitle?: string;
    /** Override the 👍/👎 emoji glyphs — pass `() => <ThumbsUpIcon />` etc.
     *  when you want SVG icons. Default keeps the emoji glyphs. */
    renderPositive?: () => ReactNode;
    renderNegative?: () => ReactNode;
}

/**
 * Thumbs-up / thumbs-down chip pair. Captures user feedback on whatever the
 * caller scopes it to (a submission, a hint, etc.) and passes the rating to
 * `onRate`. Owns optimistic state; remembers the chosen value across re-renders
 * via `initialRating`.
 *
 * Wherever this is used today the rating is funneled into the app-feedback
 * stream on the backend, but the component itself is agnostic — `onRate`
 * decides the destination.
 *
 * Two extension points keep the component reusable across our richer flows:
 *   - `onAfterRate` runs after persist for hosts that need a follow-up
 *     (e.g. an optional-comment popup on the student feedback page).
 *   - `renderPositive` / `renderNegative` swap the emoji glyphs for icon
 *     components without forking the rest of the markup/state.
 */
export function ThumbsRating({
    initialRating,
    onRate,
    onAfterRate,
    size = 'md',
    positiveTitle = 'Helpful',
    negativeTitle = 'Not helpful',
    renderPositive,
    renderNegative,
}: ThumbsRatingProps) {
    const [rating, setRating] = useState<ThumbsValue | null>(initialRating ?? null);
    const [submitting, setSubmitting] = useState(false);

    const handleRate = async (next: ThumbsValue) => {
        setSubmitting(true);
        try {
            await onRate(next);
            setRating(next);
            if (onAfterRate) {
                // Run the host's follow-up but don't let its failure affect the
                // already-persisted rating — onRate succeeded.
                try { await onAfterRate(next); } catch { /* host-owned error */ }
            }
        } catch {
            // onRate failure: silent — rating is non-critical UX.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <span className={`thumbs-rating thumbs-rating--${size}`}>
            <button
                type="button"
                className={`thumbs-rating-btn ${rating === 'positive' ? 'active positive' : ''}`}
                onClick={() => handleRate('positive')}
                disabled={submitting}
                title={positiveTitle}
            >
                {renderPositive ? renderPositive() : '👍'}
            </button>
            <button
                type="button"
                className={`thumbs-rating-btn ${rating === 'negative' ? 'active negative' : ''}`}
                onClick={() => handleRate('negative')}
                disabled={submitting}
                title={negativeTitle}
            >
                {renderNegative ? renderNegative() : '👎'}
            </button>
        </span>
    );
}
